import * as THREE from 'three/webgpu';
import { PointsMaterialFactory } from '../../material/PointsMaterialFactory';
import type { Viewer } from '../../viewer/Viewer';

/**
 * 基准点预览对象名称枚举。
 */
enum ReferencePointPreviewObjectName {
  /** 预览根组名称。 */
  RootGroup = '__reference_point_preview_group__',
  /** 预览点名称。 */
  Sprite = '__reference_point_preview_sprite__',
}

/**
 * 基准点预览默认缩放。
 *
 * 说明：
 * 1. 预览点需要与正式基准点保持一致的屏幕视觉尺寸；
 * 2. 正式基准点当前使用 `Sprite` 且缩放固定为 `1`；
 * 3. 预览层沿用同一尺寸基准，避免提交前后视觉跳变。
 */
const REFERENCE_POINT_PREVIEW_DEFAULT_SCALE = 0.008;

/**
 * 基准点预览渲染器。
 *
 * 职责：
 * 1. 仅负责临时预览点的场景挂载、更新与销毁；
 * 2. 不创建正式基准点对象，也不参与设计树管理；
 * 3. 统一收口预览资源释放路径，避免控制器直接拼装可视对象。
 */
export class ReferencePointPreviewRenderer {
  /** 视图实例。 */
  private readonly viewer: Viewer;
  /** 当前预览根组。 */
  private previewGroup: THREE.Group | null = null;

  /**
   * @param viewer 视图实例。
   */
  public constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * 渲染预览点集合。
   *
   * @param points 预览点集合。
   */
  public render(points: THREE.Vector3[]): void {
    this.clear();
    if (points.length === 0) {
      return;
    }

    const group = new THREE.Group();
    group.name = ReferencePointPreviewObjectName.RootGroup;

    points.forEach((point) => {
      // 每个预览点都独立持有材质克隆，确保销毁时不会误伤材质工厂缓存。
      const spriteMaterial = PointsMaterialFactory.getDatumPointDefaultSpriteMaterial()
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.name = ReferencePointPreviewObjectName.Sprite;
      sprite.position.copy(point.clone());
      sprite.scale.set(
        REFERENCE_POINT_PREVIEW_DEFAULT_SCALE,
        REFERENCE_POINT_PREVIEW_DEFAULT_SCALE,
        REFERENCE_POINT_PREVIEW_DEFAULT_SCALE,
      );
      group.add(sprite);
    });

    this.previewGroup = group;
        this.viewer.addObject(group);
  }

  /**
   * 清理当前预览对象。
   */
  public clear(): void {
    if (!this.previewGroup) {
      return;
    }

    this.viewer.scene.remove(this.previewGroup);
    this.previewGroup.traverse((object) => {
      const sprite = object as THREE.Sprite;
      const material = sprite.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else if (material instanceof THREE.Material) {
        material.dispose();
      }
    });
    this.previewGroup.clear();
    this.previewGroup = null;
  }

  /**
   * 释放渲染器资源。
   */
  public dispose(): void {
    this.clear();
  }
}
